import './App.css'
import { Navigate, Route, Routes, Outlet, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from './redux/store'
import { clearFilters as clearEventReportFilters } from './redux/reports/event/invitedTeamMemberPerEvent/slice'
import { clearFilters as clearTeamMemberFilters } from './redux/teamMember/slice'
import { clearFilters as clearTaskReportFilters } from './redux/reports/task/assignedTeamMemberPerTask/slice'
import { loginFailure } from './redux/auth/slice'
import { selectAuthLoading } from './redux/auth/selector'

import GoogleLogin from './auth/GoogleLogin'
import Redirect from './auth/Redirect'
import OverviewPage from './pages/OverviewPage'
import ManageEventPage from './pages/ManageEventPage'
import ViewEventPage from './pages/ViewEventPage'
import SweetAlertPanel from './Components/Common/SweetAlert/SweetAlertPanel'
import ColorPanel from './Components/Common/Color/ColorPanel'
import TypographyPanel from './Components/Common/Typography/TypographyPanel'
import ButtonPanel from './Components/Common/Button/ButtonPanel'
import BadgePanel from './Components/Common/Badge/BadgePanel'
import ModalPanel from './Components/Common/Modal/ModalPanel'
import SwitchPanel from './Components/Common/Switch/SwitchPanel'
import ProtectedRoute from './Components/Common/Route/ProtectedRoute'
import CombinedProviders from './CombinedProvide'
import ViewTaskPage from './pages/ViewTaskPage'
import ManageTaskPage from './pages/ManageTaskPage'
import UnauthorizedPage from './pages/error/UnauthorizedPage'
import ComingSoonPage from './pages/error/CommingSoonPage'
import NoAccessPage from './pages/error/NoAccessPage'
import EventReportPage from './pages/Report/EventReportPage'
import TaskReportPage from './pages/Report/TaskReportPage'
import ViewEventReport from './Components/Report/EventReport/ViewEvent/index'
import ViewTaskReport from './Components/Report/TaskReport/ViewTask/index'
// import { useEffect } from 'react'
import Settings from './pages/Settings'
import TeamRoster from './pages/TeamRoster'
import Dashboard from './Components/Common/Dashboard'
import NotFoundPage from './pages/error/NotFoundPage'
import UserGuidePage from './pages/UserGuidePage'
import FeedbackPage from './pages/FeedbackPage'

function App() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const authLoading = useAppSelector(selectAuthLoading)

  // Initialize authentication state on app startup
  useEffect(() => {
    // Add a timeout to clear stuck loading state
    const timeout = setTimeout(() => {
      if (authLoading) {
        console.log('Authentication timeout - clearing stuck loading state')
        dispatch(loginFailure({
          message: 'Authentication timeout',
          details: 'Please try logging in again'
        }))
      }
    }, 10000) // 10 second timeout

    return () => clearTimeout(timeout)
  }, [dispatch, authLoading])

  useEffect(() => {
    // Clear filters when route changes, but not for the same route
    // This prevents clearing filters when actions like CSV export are performed
    const currentPath = location.pathname
    const isEventReportPage = currentPath.startsWith('/reports/event')
    const isTaskReportPage = currentPath.startsWith('/reports/task')
    const isTeamMemberPage = currentPath.includes('team')

    // Only clear filters if we're navigating away from the respective report pages
    if (!isEventReportPage) {
      dispatch(clearEventReportFilters())
    }
    if (!isTeamMemberPage) {
      dispatch(clearTeamMemberFilters())
    }
    if (!isTaskReportPage) {
      dispatch(clearTaskReportFilters())
    }
  }, [location.pathname, dispatch])

  // Define routes dynamically
  const routes = [
    {
      path: '/overview',
      component: <OverviewPage />,
    },
    {
      path: '/events',
      component: <ViewEventPage />,
    },
    {
      path: '/events/:registrationCode',
      component: <ViewEventPage />,
    },
    {
      path: '/orders',
      component: <ComingSoonPage />,
    },
    {
      path: 'tasks',
      component: <ViewTaskPage />,
    },
    {
      path: 'color',
      component: <ColorPanel />,
    },
    {
      title: 'TYPOGRAPHY',
      component: <TypographyPanel />,
    },
    {
      path: 'buttons',
      component: <ButtonPanel />,
    },
    {
      path: 'badge',
      component: <BadgePanel />,
    },
    {
      path: 'modal',
      component: <ModalPanel />,
    },
    {
      path: 'switch',
      component: <SwitchPanel />,
    },
    {
      path: 'sweetAlert',
      component: <SweetAlertPanel />,
    },
    {
      path: '/unauthorized',
      component: <UnauthorizedPage />,
    },
    {
      path: '/settings',
      component: <Settings />,
    },
    {
      path: '/user-guide',
      component: <UserGuidePage />,
    },
    {
      path: '/support',
      component: <FeedbackPage />,
    },
  ]

  // Routes accessible by both admin and manager
  const sharedAdminManagerRoutes = [
    {
      path: '/events/manage-events',
      component: <ManageEventPage />,
    },
    {
      path: 'tasks/manage-tasks',
      component: <ManageTaskPage />,
    },
    {
      path: 'reports/event',
      component: <EventReportPage />,
    },
    {
      path: 'reports/task',
      component: <TaskReportPage />,
    },
    {
      path: 'reports/event/:id',
      component: <ViewEventReport />,
    },
    {
      path: 'reports/task/:id',
      component: <ViewTaskReport />,
    },
  ]

  // Routes accessible by admin only
  const adminOnlyRoutes = [
    {
      path: '/order/manage-orders',
      component: <ComingSoonPage />,
    },
    {
      path: 'team-roster',
      component: <TeamRoster />,
    },
    {
      path: 'expenses',
      component: <ComingSoonPage />,
    },
  ]

  // const navigate = useNavigate()

  // useEffect(() => {
  //   const SESSION_TIMEOUT = 30 * 60 * 1000 // 30 minutes
  //   const handleSessionTimeout = () => {
  //     sessionStorage.clear()
  //     localStorage.clear()
  //     navigate('/login', { replace: true })
  //   }

  //   const resetTimeout = () => {
  //     clearTimeout(sessionTimer)
  //     sessionTimer = setTimeout(handleSessionTimeout, SESSION_TIMEOUT)
  //   }

  //   window.addEventListener('mousemove', resetTimeout)
  //   window.addEventListener('keydown', resetTimeout)

  //   let sessionTimer = setTimeout(handleSessionTimeout, SESSION_TIMEOUT)

  //   return () => {
  //     window.removeEventListener('mousemove', resetTimeout)
  //     window.removeEventListener('keydown', resetTimeout)
  //     clearTimeout(sessionTimer)
  //   }
  // }, [])

  return (
    <CombinedProviders>
      <Routes>
        <Route path='/login' element={<GoogleLogin />} />
        <Route path='/redirect' element={<Redirect />} />
        <Route path='/no-access' element={<NoAccessPage />} />
        <Route path='/unauthorized' element={<UnauthorizedPage />} />
        <Route path='*' element={<NotFoundPage />} />

        <Route element={<ProtectedRoute allowedRoles={['teamMember', 'admin', 'manager']} />}>
          <Route
            element={
              <Dashboard>
                <Outlet />
              </Dashboard>
            }
          >
            <Route path='/' element={<Navigate to='/overview' replace />} />
            {routes.map(({ path, component }) => (
              <Route key={path} path={path} element={component} />
            ))}
          </Route>
        </Route>

        <Route element={<ProtectedRoute allowedRoles={['admin', 'manager']} />}>
          <Route
            element={
              <Dashboard>
                <Outlet />
              </Dashboard>
            }
          >
            <Route path='/' element={<Navigate to='/overview' replace />} />
            {sharedAdminManagerRoutes.map(({ path, component }) => (
              <Route key={path} path={path} element={component} />
            ))}
          </Route>
        </Route>

        <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
          <Route
            element={
              <Dashboard>
                <Outlet />
              </Dashboard>
            }
          >
            <Route path='/' element={<Navigate to='/overview' replace />} />
            {adminOnlyRoutes.map(({ path, component }) => (
              <Route key={path} path={path} element={component} />
            ))}
          </Route>
        </Route>
      </Routes>
    </CombinedProviders>
  )
}

export default App
